import React, { useEffect, useState } from 'react';
import { deliveryAPI, PropertyData, PropertyPhoto } from '../../services/deliveryAPI';
import { resizeImage } from '../../utils/resizeImage';
import { parseApiError } from '../../utils/apiErrors';
import ZoomImage from './feed/ZoomImage';

/**
 * The property access questions and photos - ONE form, used by onboarding step 4 and by
 * the Profile's "Delivery access" section (Brad, 2026-09-23: the onboarding answers
 * "should be in the profile for the customer to modify if needed. Add new photos, change
 * lock codes"). One component so the two cannot drift, the same rule as the feed picker.
 *
 * Controlled for the answers (`form` / `onChange`); the photos it manages itself, since a
 * photo is saved the moment it is added, in both places.
 */

const RED = '#B91C1C';

export const DROP_OPTIONS: { v: NonNullable<PropertyData['drop_location']>; l: string }[] = [
  { v: 'front_door', l: 'Front door' },
  { v: 'back_door', l: 'Back door' },
  { v: 'garage', l: 'Garage' },
  { v: 'other', l: 'Somewhere else (add a photo)' },
];

export const PHOTO_KINDS = [
  { v: 'drop', l: 'Where the bags go' },
  { v: 'gate', l: 'Gate / entry' },
  { v: 'storage', l: 'Feed storage' },
  { v: 'coop', l: 'Coop' },
  { v: 'feeder', l: 'Feeder' },
  { v: 'waterer', l: 'Waterer' },
  { v: 'grit', l: 'Grit' },
  { v: 'shell', l: 'Oyster shell' },
  { v: 'other', l: 'Other' },
];
const KIND_LABELS: Record<string, string> = Object.fromEntries(PHOTO_KINDS.map((k) => [k.v, k.l]));
KIND_LABELS.latch = 'Latch';
KIND_LABELS.dog = 'Dog';

interface Props {
  form: PropertyData;
  onChange: (next: PropertyData) => void;
  // Onboarding shows a count of photos just added; the Profile shows every photo on file
  // with Remove. Both can add.
  showExistingPhotos?: boolean;
}

const PropertyAccessForm: React.FC<Props> = ({ form, onChange, showExistingPhotos }) => {
  const [photoKind, setPhotoKind] = useState('storage');
  const [photos, setPhotos] = useState<PropertyPhoto[]>([]);
  const [added, setAdded] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<number | null>(null);
  const [photoError, setPhotoError] = useState('');

  useEffect(() => {
    if (!showExistingPhotos) return;
    deliveryAPI.getPropertyPhotos().then(setPhotos).catch(() => setPhotos([]));
  }, [showExistingPhotos]);

  const set = (k: keyof PropertyData, v: unknown) => onChange({ ...form, [k]: v });

  const onPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setPhotoError('');
    setUploading(true);
    try {
      const { file: out, tooLarge } = await resizeImage(file);
      if (tooLarge) {
        setPhotoError('That photo is too large even after resizing. Please try a different photo.');
        return;
      }
      const saved = await deliveryAPI.uploadPhoto(photoKind, out);
      setAdded((n) => n + 1);
      if (showExistingPhotos) {
        if (saved && saved.id) setPhotos((prev) => [...prev, saved as PropertyPhoto]);
        else deliveryAPI.getPropertyPhotos().then(setPhotos).catch(() => undefined);
      }
    } catch (err) {
      setPhotoError(parseApiError(err, { fallback: 'That photo could not be uploaded. Please try another.' }).message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (photo: PropertyPhoto) => {
    const what = KIND_LABELS[photo.kind] || 'this';
    if (!window.confirm(`Remove the ${what.toLowerCase()} photo? Your driver won't see it next time.`)) return;
    setRemoving(photo.id);
    setPhotoError('');
    try {
      await deliveryAPI.deletePropertyPhoto(photo.id);
      setPhotos((prev) => prev.filter((p) => p.id !== photo.id));
    } catch {
      setPhotoError("That photo wasn't removed. Please try again.");
    } finally {
      setRemoving(null);
    }
  };

  const field = (label: string, k: keyof PropertyData, placeholder = '') => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
      <input
        value={(form[k] as string) || ''}
        onChange={(e) => set(k, e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        style={{ width: '100%', boxSizing: 'border-box', padding: 10, minHeight: 44, fontSize: 16, borderRadius: 8, border: '1px solid #d1d5db' }}
      />
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Where should the driver leave the bags?</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {DROP_OPTIONS.map((o) => {
            const on = form.drop_location === o.v;
            return (
              <button key={o.v} type="button" onClick={() => set('drop_location', o.v)}
                style={{ minHeight: 44, padding: '10px 14px', borderRadius: 8, border: `1px solid ${on ? RED : '#d1d5db'}`, background: on ? '#fef2f2' : '#fff', color: '#111', fontWeight: on ? 700 : 500, cursor: 'pointer' }}>
                {o.l}
              </button>
            );
          })}
        </div>
      </div>
      {form.drop_location === 'garage' && field('Garage code', 'garage_code', 'if the door needs one')}
      {form.drop_location === 'other' && (
        <p style={{ color: '#92400e', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}>
          Add a photo of the spot below (choose "Where the bags go") so the driver can find it.
        </p>
      )}
      {field('Where does the feed go?', 'feed_storage_location', 'e.g. bin in the garage, left of the door')}
      {field('Gate location', 'gate_location', 'e.g. side gate off the driveway')}
      {field('Gate code', 'gate_code', 'if any')}
      {field('Latch / access notes', 'latch_notes')}

      <div style={{ display: 'flex', gap: 16, margin: '8px 0 12px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#374151', minHeight: 44 }}>
          <input type="checkbox" checked={!!form.dog_present} onChange={(e) => set('dog_present', e.target.checked)} /> Dog on the property
        </label>
        {form.dog_present && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#374151', minHeight: 44 }}>
            <input type="checkbox" checked={!!form.dog_loose} onChange={(e) => set('dog_loose', e.target.checked)} /> Sometimes loose
          </label>
        )}
      </div>
      {form.dog_present && field('About the dog', 'dog_notes', 'name, temperament, where it stays')}

      {field('Feeder location', 'feeder_location')}
      {field('Waterer location', 'waterer_location')}
      {field('Grit location', 'grit_location', 'e.g. hanging dish inside the coop door')}
      {field('Oyster shell location', 'shell_location', 'e.g. next to the grit')}
      {field('Anything else we should know?', 'notes')}

      <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginTop: 8 }}>
        <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Photos</div>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#6b7280' }}>
          They help your driver find the gate, the feed storage and everything else.
        </p>
        {photoError && (
          <div style={{ background: '#fee', color: '#b91c1c', border: '1px solid #fcc', borderRadius: 8, padding: 10, margin: '0 0 10px' }}>{photoError}</div>
        )}
        {showExistingPhotos && photos.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 12 }}>
            {photos.map((photo) => (
              <div key={photo.id} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', padding: 8 }}>
                <ZoomImage src={photo.url} alt={KIND_LABELS[photo.kind] || photo.kind} size={104} fit="cover" />
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 6 }}>{KIND_LABELS[photo.kind] || photo.kind}</div>
                {photo.label && <div style={{ fontSize: 12, color: '#6b7280' }}>{photo.label}</div>}
                <button type="button" onClick={() => remove(photo)} disabled={removing !== null}
                  style={{ marginTop: 6, minHeight: 36, padding: '0 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#b91c1c', fontSize: 13, cursor: 'pointer' }}>
                  {removing === photo.id ? 'Removing…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={photoKind} onChange={(e) => setPhotoKind(e.target.value)} aria-label="What the photo shows"
            style={{ minHeight: 44, padding: 8, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 15 }}>
            {PHOTO_KINDS.map((k) => <option key={k.v} value={k.v}>{k.l}</option>)}
          </select>
          <label style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', padding: '0 14px', borderRadius: 8, background: '#fef2f2', border: `1px solid ${RED}`, cursor: 'pointer', color: RED, fontWeight: 600 }}>
            {uploading ? 'Uploading…' : '+ Add photo'}
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} disabled={uploading} style={{ display: 'none' }} />
          </label>
          {!showExistingPhotos && added > 0 && <span style={{ color: '#166534', fontSize: 13 }}>{added} photo{added > 1 ? 's' : ''} added</span>}
        </div>
      </div>
    </div>
  );
};

export default PropertyAccessForm;
